import { RefObject, useCallback, useMemo } from 'react';
import { getEndpointWithParams } from '@/routes/config';
import { Candidate } from '@/services/candidates';
import { PersonType } from '@/types/api';
import { Paths } from '@/types/routes';
import * as Styles from './CandidateSearchList.styles';
import Loading from '../general/Loading';

export type CandidateSearchListProps = {
  candidates: Candidate[];
  isLoading: boolean;
  hasFetched: boolean;
  hasError: boolean;
  containerRef?: RefObject<HTMLDivElement>;
};

const CandidateSearchList = ({
  candidates,
  isLoading,
  hasError,
  hasFetched,
  containerRef,
}: CandidateSearchListProps): JSX.Element => {
  const doesNotHaveCandidates = useMemo(() => {
    return !candidates.length && hasFetched && !isLoading;
  }, [candidates.length, hasFetched, isLoading]);

  const hasCandidates = useMemo(() => {
    return !!candidates.length && hasFetched && !isLoading;
  }, [candidates.length, hasFetched, isLoading]);

  const thereWasError = useMemo(() => {
    return hasError && !isLoading;
  }, [hasError, isLoading]);

  const renderCandidates = useCallback((candidate: PersonType, index: number) => {
    const candidateLink = getEndpointWithParams(Paths.CANDIDATE_DETAILS, {
      id: candidate.id,
    });

    return (
      <Styles.ListItem data-testid={`candidate-result-item-${index}`} key={candidate.id}>
        <Styles.ListItemLink data-testid={`candidate-result-item-${index}-link`} to={candidateLink}>
          <Styles.CustomUserInitials
            pictureUrl={candidate?.pictureUrl ?? ''}
            firstName={candidate.firstName}
            lastName={candidate?.lastName ?? ''}
          />
          <Styles.UserDetails>
            <Styles.FirstRow>
              <Styles.CandidateName>
                {candidate.firstName} {candidate?.lastName}
              </Styles.CandidateName>
              {candidate.status && <Styles.CustomStatusTag status={candidate.status} />}
            </Styles.FirstRow>
            <div className="flex">
              <Styles.CandidateEmail>{candidate.email}</Styles.CandidateEmail>
              <Styles.Text>{candidate.mobilePhone}</Styles.Text>
            </div>
          </Styles.UserDetails>
        </Styles.ListItemLink>
      </Styles.ListItem>
    );
  }, []);

  return (
    <div ref={containerRef} id="candidate-search-list" data-testid="candidate-search-list">
      {isLoading && (
        <Styles.Box data-testid="candidate-search-loading">
          <Styles.LoadingArea>
            <Loading size="16px" />
          </Styles.LoadingArea>
        </Styles.Box>
      )}

      {doesNotHaveCandidates && (
        <Styles.Box data-testid="candidate-search-not-found">
          <Styles.NotFound>No candidates found.</Styles.NotFound>
        </Styles.Box>
      )}

      {thereWasError && (
        <Styles.Box data-testid="candidate-search-error">
          <Styles.ErrorMessage>There was an error. Please, try again.</Styles.ErrorMessage>
        </Styles.Box>
      )}

      {hasCandidates && (
        <Styles.Box data-testid="candidate-search-results">
          <Styles.List>{candidates.map(renderCandidates)}</Styles.List>
        </Styles.Box>
      )}
    </div>
  );
};

export default CandidateSearchList;
